import { IconButton, Stack, Tooltip } from '@mui/material';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  BottomNavigation,
  NavDivider,
  PageDivider,
  PageInput,
  PageInputWrapper,
  PageNavigationContainer,
  TotalPages,
} from './styles';

import { useTranslation } from 'react-i18next';

export interface PdfPageNavigationProps {
  currentPage: number;
  totalPages: number;
  /**
   * Horizontal shift in px, so a host with an open side panel can keep the bar
   * centred on the visible area. Defaults to 0 (centred on the container).
   */
  offsetPx?: number;
  onPageChange: (page: number) => void;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

const PdfPageNavigation = ({
  currentPage,
  totalPages,
  offsetPx = 0,
  onPageChange,
  zoom,
  onZoomIn,
  onZoomOut,
}: PdfPageNavigationProps) => {
  const { t } = useTranslation('dashboard', { keyPrefix: 'documents' });
  const [pageInput, setPageInput] = useState(`${currentPage}`);

  useEffect(() => {
    setPageInput(`${currentPage}`);
  }, [currentPage]);

  const commitPageChange = () => {
    const value = parseInt(pageInput, 10);
    if (!isNaN(value) && value >= 1 && value <= totalPages) {
      onPageChange(value);
      return;
    }
    setPageInput(`${currentPage}`);
  };

  const handlePageInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(event.target.value);
  };

  return (
    <PageNavigationContainer offsetPx={offsetPx}>
      <BottomNavigation>
        {/* Page Input */}
        <PageInputWrapper>
          <PageInput
            value={pageInput}
            onChange={handlePageInputChange}
            onBlur={commitPageChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                commitPageChange();
              }
            }}
            variant='outlined'
            size='small'
            inputProps={{
              min: 1,
              max: totalPages,
            }}
          />
          <PageDivider>/</PageDivider>
          <TotalPages>{totalPages}</TotalPages>
        </PageInputWrapper>

        <NavDivider orientation='vertical' flexItem />

        {/* Zoom Controls */}
        <Stack direction='row' alignItems='center'>
          <Tooltip title={t('zoom_out')}>
            <IconButton onClick={onZoomOut} disabled={zoom <= 0.5}>
              <ZoomOut size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('zoom_in')}>
            <IconButton onClick={onZoomIn} disabled={zoom >= 2}>
              <ZoomIn size={16} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* The view-mode toggle below has been commented out since before this component moved
            to `shared`. Its `viewMode` / `onViewModeChange` props were dead (nothing read them),
            so they were dropped rather than dragged down here — the editor's `ViewMode` type
            stays in the editor, where its four real users live. Re-add them alongside this JSX
            if the toggle is ever switched back on.

        <NavDivider />

        <ViewModeToggle
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && onViewModeChange(newMode)}
          aria-label={t('view_mode')}
        >
          <Tooltip title={t('scroll_mode')}>
            <ViewModeButton value='scroll' aria-label={t('scroll_mode')}>
              <ScrollModeIcon />
            </ViewModeButton>
          </Tooltip>
          <Tooltip title={t('all_pages')}>
            <span>
              <ViewModeButton value='all' aria-label={t('all_pages')} disabled>
                <AllPagesIcon />
              </ViewModeButton>
            </span>
          </Tooltip>
          <Tooltip title={t('comparison_mode')}>
            <span>
              <ViewModeButton value='comparison' aria-label={t('comparison_mode')} disabled>
                <ComparisonModeIcon />
              </ViewModeButton>
            </span>
          </Tooltip>
        </ViewModeToggle> */}
      </BottomNavigation>
    </PageNavigationContainer>
  );
};

export default PdfPageNavigation;
